import Promise from 'bluebird';
import queryString from 'query-string';
import { fetchPermissions, getCurrentUser } from './user';
import { getMetadataFromParams } from './utils';
import {
  checkForVanityUrl,
  emailCampaignRedirect,
  redirectToAppStoreFromReferralPage,
  getReferralCodeFromCallInvite,
  redirectLegacyStationPageToVanityUrl,
  redirectToPath,
  redirectToUrl,
  openInAppRedirect,
  saveReferralIp,
  setCanonicalEpisodePathAndGetReferralCodeForEpisode,
  storeCampaign,
} from './referralRedirect';
import { getAMPRoutes } from './ampRoutes';
import USER_PERMISSIONS from '../helpers/userPermissions';
import { windowUndefined } from '../helpers/serverRenderingUtils';
import withOnEnter from './withOnEnter';
import withVanityHeader from './withVanityHeader';
import withBlockingOnEnter from './withBlockingOnEnter';
import AppContainer from './components/AppContainer';
import AMPJobsPage from './components/AMPJobsPage';
import AMPHomePage from './components/AMPHomePage';
import CallJoinContainer from './components/CallJoinContainer';
import CohostJoinContainer from './components/CohostJoinContainer';
import EpisodeOldContainer from './components/EpisodeOldContainer';
import StationListContainer from './components/StationListContainer';
import PartnerContainer from './components/PartnerContainer';
import RecordOnMobileContainer from './components/RecordOnMobileContainer';
import RedirectingInterstitial from './components/RedirectingInterstitial';
import ResetPasswordContainer from './components/ResetPasswordContainer';
import { SupportersCancelScreenContainer } from './screens/SupportersCancelScreen';
// import SupportStationContainer from './components/SupportStationContainer';
import SignupPageContainer from './components/SignupPageContainer';
// TODO: re-enable as async code bundles\
import { ImportScreenContainer } from './screens/ImportScreen';
import OnboardingCreateAccountContainer from './components/OnboardingCreateAccountContainer';

import {
  ArchiveContainer,
  AuthRouter,
  EpisodeEmbedContainer,
  EpisodePlayerContainer,
  PartnerAnalyticsContainer, // TODO: rename? This is specifically video transcription requests
  PartnerEpisodeContainer,
  PartnerEpisodesContainer,
  ProfileContainer,
  ProfileEpisodeContainer,
  StationEmbedContainer,
} from './asyncRouteComponents';
import DMCAPage from './components/DMCAPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsScreen from './screens/TermsScreen';
import MonetizationTermsPage from './components/MonetizationTermsPage';
import { SponsorshipsPageScreenContainer } from './screens/SponsorshipsPageScreen';
import LoginPageContainer from './components/LoginPageContainer';
import NotFoundContainer from './components/NotFoundContainer';
import { AdAdvertiserCreatePaymentScreenContainer } from './screens/AdAdvertiserCreatePaymentScreen';

// Wrap routes with HOCs to account for redirects
// 'Exact' routes prevents multiple matches from child routes,
// but otherwise need to arrange by most specific first
// (see https://www.npmjs.com/package/react-router-config#renderroutesroutes-extraprops--)

// static AMP routes redirect to dashboard when authenticated
export function getRoutesWithStore(store, baseUrl = '') {
  const routes = [
    {
      component: withOnEnter(storeCampaign(store), AppContainer),
      routes: [
        ...getAMPRoutes().map(route =>
          route.path === '/'
            ? Object.assign({}, route, {
                component: withOnEnter(
                  redirectPublicPathToAuthedPath(store, '/dashboard'),
                  route.component
                ),
              })
            : route
        ),
        {
          path: '/404',
          component: NotFoundContainer,
          exact: true,
        },
        {
          path: '/activity',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/advertiser/:adVanitySlug/:webAdAdvertiserId/payment/create',
          component: AdAdvertiserCreatePaymentScreenContainer,
          exact: true,
        },
        {
          path: '/jobs',
          component: AMPJobsPage,
          exact: true,
        },
        {
          path: '/app',
          component: withOnEnter(
            redirectToUrl(store, 'https://smarturl.it/anchor-tw'),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/archive',
          component: ArchiveContainer,
          exact: true,
        },
        {
          canonicalPath: '/',
          path: '/audiobank/:audioId/:media',
          component: AMPHomePage,
          exact: true,
        },
        // TODO: deprecate
        {
          path: '/beta',
          component: redirectRoute(store, '/signup'),
          exact: true,
        },
        // TODO: deprecate
        {
          path: '/betacode',
          component: redirectRoute(store, '/singup'),
          exact: true,
        },
        {
          path: '/canvas',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/clip',
          component: redirectRoute(store, '/dashboard'),
          exact: true,
        },
        {
          path: '/clips',
          component: redirectRoute(store, '/dashboard'),
          exact: true,
        },
        {
          path: '/clipper',
          component: redirectRoute(store, '/dashboard'),
          exact: true,
        },
        {
          path: '/clipper',
          component: redirectRoute(store, '/dashboard'),
          exact: true,
        },
        {
          path: '/clockwise',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/connected',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/createaccount',
          component: redirectRoute(store, '/dashboard'),
          exact: true,
        },
        {
          path: '/creation/record',
          canonicalPath: '/',
          component: RecordOnMobileContainer,
          exact: true,
        },
        {
          path: '/creation/:tab',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/creation/:tab/:id',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        // Auth Dashboard routes
        {
          path: '/dashboard',
          component: withOnEnter(
            redirectAuthPathToPublicPath(store, baseUrl),
            AuthRouter
          ),
        },
        {
          path: '/discover',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/dmca',
          component: DMCAPage,
          exact: true,
        },
        {
          path: '/download',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/e/:stationId',
          component: StationEmbedContainer,
          exact: true,
        },
        {
          path: '/emailcampaign',
          component: withOnEnter(
            emailCampaignRedirect(store),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/grouping/:id',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        // deprecating listen
        {
          path: '/listen',
          component: redirectRoute(store, '/'),
          exact: true,
        },
        {
          path: '/login',
          exact: true,
          component: LoginPageContainer,
        },
        {
          path: '/mac',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/me',
          component: withOnEnter(
            openInAppRedirect(store),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/me/:tab',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/onboarding',
          component: redirectRoute(store, '/signup'),
          exact: true,
          // Onboarding routes
        },
        {
          path: '/onboarding/rss',
          component: redirectRoute(store, '/switch'),
          exact: true,
        },
        {
          path: '/onboarding/*',
          component: redirectRoute(store, '/signup'),
          exact: true,
        },
        {
          path: '/partner',
          component: PartnerContainer,
          routes: [
            {
              path: '/partner/analytics',
              component: PartnerAnalyticsContainer,
              exact: true,
            },
            {
              path: '/partner/episodes/:episodeId',
              component: withOnEnter(
                redirectAuthPathToPublicPath(
                  store,
                  baseUrl,
                  USER_PERMISSIONS.EPISODES
                ),
                PartnerEpisodeContainer
              ),
              exact: true,
            },
            {
              path: '/partner/episodes', // index route
              component: withOnEnter(
                redirectAuthPathToPublicPath(
                  store,
                  baseUrl,
                  USER_PERMISSIONS.EPISODES
                ),
                PartnerEpisodesContainer
              ),
              exact: true,
            },
          ],
        },
        {
          path: '/podcast',
          component: withOnEnter(
            redirectToUrl(store, 'https://smarturl.it/anchorpodcasts'),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/start',
          component: withOnEnter(
            redirectToUrl(store, 'https://smarturl.it/anchorpodcasts'),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/skillshare',
          component: withOnEnter(
            redirectToUrl(store, 'https://smarturl.it/anchorpodcasts'),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/ashley-graham',
          component: withOnEnter(
            redirectToUrl(store, '/prettybigdealpod'),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/ashleygraham',
          component: withOnEnter(
            redirectToUrl(store, '/prettybigdealpod'),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/switch',
          component: withOnEnter(
            redirectPublicPathToAuthedPath(store, '/dashboard'),
            ImportScreenContainer
          ),
          exact: true,
        },
        {
          path: '/switch/signup',
          component: withOnEnter(
            redirectPublicPathToAuthedPath(store, '/dashboard'),
            OnboardingCreateAccountContainer
          ),
          exact: true,
        },
        {
          path: '/podcastlab',
          component: withOnEnter(
            redirectToUrl(store, 'https://lab.anchor.fm'),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/privacy',
          component: PrivacyPolicyPage,
          exact: true,
        },
        {
          path: '/r/:vanityName/:referralCode',
          canonicalPath: '/',
          component: withBlockingOnEnter(
            redirectToAppStoreFromReferralPage(store, baseUrl),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/r/*',
          component: redirectRoute(store, '/'),
          exact: true,
        },
        {
          path: '/r',
          component: redirectRoute(store, '/'),
          exact: true,
        },
        {
          path: '/reset/:code',
          component: ResetPasswordContainer,
          exact: true,
        },
        {
          path: '/rr/:episodeId',
          component: EpisodeOldContainer,
          exact: true,
        },
        {
          path: '/signup',
          component: withOnEnter(
            redirectPublicPathToAuthedPath(store, '/dashboard'),
            SignupPageContainer
          ),
        },
        {
          path: '/stations',
          component: StationListContainer,
          exact: true,
        },
        {
          path: '/stripe/connected',
          component: withOnEnter(
            openInAppRedirect(store),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/stripe/connectionfailed',
          component: withOnEnter(
            openInAppRedirect(store),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/podcastimport',
          component: redirectRoute(store, '/switch'),
        },
        {
          path: '/s/:stationId',
          component: withBlockingOnEnter(
            redirectLegacyStationPageToVanityUrl(store, baseUrl),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/tos',
          component: TermsScreen,
          exact: true,
        },
        {
          path: '/tos/monetization',
          component: MonetizationTermsPage,
          exact: true,
        },
        {
          path: '/sponsorships',
          component: SponsorshipsPageScreenContainer,
          exact: true,
        },
        {
          path: '/transcriptions/:id',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/u/:userId',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/upgrade',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/videos/:id',
          canonicalPath: '/',
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/', // index route
          component: AMPHomePage,
          exact: true,
        },
        {
          path: '/:vanitySlug/call/:callJoinCode', // index route
          canonicalPath: '/',
          component: withOnEnter(
            getReferralCodeFromCallInvite(store, baseUrl),
            CallJoinContainer
          ),
          exact: true,
        },
        {
          path: '/:vanitySlug/call', // index route
          component: redirectRoute(store, '/'),
          exact: true,
        },
        {
          path: '/:vanitySlug/cohost/:webCohostTopicId',
          // TODO: no callJoinCode yet
          component: withOnEnter(
            getReferralCodeFromCallInvite(store, baseUrl),
            CohostJoinContainer
          ),
          exact: true,
        },
        // redundant with /r/:vanityName/:referralCode
        {
          path: '/:vanityName/r/:referralCode',
          canonicalPath: '/',
          component: withBlockingOnEnter(
            redirectToAppStoreFromReferralPage(store, baseUrl),
            RedirectingInterstitial
          ),
          exact: true,
        },
        {
          path: '/:vanityName/r',
          component: redirectRoute(store, '/'),
          exact: true,
        },
        // note hyphen must be included to differentiate from 2.0 episode URLs
        // but params parsing is not smart enough to get last hyphen; we re-join these segments
        // and parse with our own code
        {
          path:
            '/:vanitySlug/episodes/:episodeHashBeforeHyphen-:episodeHashAfterHyphen/:audioCaptionAndAudioIdHash',
          component: withOnEnter(
            setCanonicalPathToEpisodeAndSetReferralCodeWithStore(store),
            withVanityHeader(ProfileEpisodeContainer)
          ),
          exact: true,
        },
        {
          path:
            '/:vanitySlug/episodes/:episodeHashBeforeHyphen-:episodeHashAfterHyphen',
          component: withOnEnter(
            setReferralCodeForEpisodeWithStore(store, baseUrl),
            withVanityHeader(ProfileEpisodeContainer)
          ),
          exact: true,
        },
        {
          path:
            '/:vanitySlug/embed/episodes/:episodeHashBeforeHyphen-:episodeHashAfterHyphen/:audioCaptionAndAudioIdHash',
          component: withOnEnter(
            setCanonicalPathToEpisodeAndSetReferralCodeWithStore(
              store,
              baseUrl
            ),
            ProfileEpisodeContainer
          ),
          exact: true,
        },
        {
          path:
            '/:vanitySlug/embed/episodes/:episodeHashBeforeHyphen-:episodeHashAfterHyphen',
          component: ProfileEpisodeContainer,
          exact: true,
        },
        {
          path: '/:vanitySlug/episodes/:episodeId/embed',
          component: EpisodeEmbedContainer,
          exact: true,
        },
        {
          path: '/:vanitySlug/episodes/:episodeId',
          component: withOnEnter(
            setCanonicalEpisodePathAndGetReferralCodeForEpisode(store),
            EpisodePlayerContainer
          ),
          exact: true,
        },
        {
          path: '/:vanitySlug/embed',
          component: withBlockingOnEnter(
            checkForVanityUrl(store, baseUrl),
            ProfileContainer
          ),
          exact: true,
        },
        {
          path: '/:vanitySlug/support',
          component: withBlockingOnEnter(
            checkForVanityUrl(store, baseUrl),
            withVanityHeader(ProfileContainer)
          ),
          exact: true,
        },
        {
          path: '/:vanitySlug/support/cancel',
          component: withBlockingOnEnter(
            checkForVanityUrl(store, baseUrl),
            SupportersCancelScreenContainer
          ),
          exact: true,
        },
        {
          path: '/*',
          component: withBlockingOnEnter(
            checkForVanityUrl(store, baseUrl),
            withVanityHeader(ProfileContainer)
          ),
          exact: true,
        },
      ],
    },
  ];
  return routes;
}

function redirectAuthPathToPublicPath(
  store,
  baseUrl = '',
  requiredPermission,
  publicPath = '/login'
) {
  return ({ history, location }) => {
    const {
      user: { permissions, user },
    } = store.getState();
    if (!user) {
      // This second user check is primarily for hot module reloading.
      // If we don't have this getCurrentUser() check, you'll be redirected
      // to the login screen after webpack dev server hot reloads, even if
      // you're technically still logged in. This is because we previously
      // would only do a check to see if the user object is not null in our
      // redux store (if statement above) when rendering signed in routes.
      (windowUndefined() ? Promise.resolve({}) : getCurrentUser()).then(
        response => {
          if (!response.user) {
            const query = {
              return_to: `${location.pathname}${location.search}`,
            };
            history.replace({
              pathname: publicPath,
              search: queryString.stringify(query),
            });
          }
        }
      );
    }
    if (requiredPermission) {
      if (!permissions) {
        // note - this does happen server-side
        // do not always check for permissions since very few routes actually require them
        return store.dispatch(fetchPermissions(baseUrl)).then(() => {
          const {
            user: { permissions },
          } = store.getState();
          redirectBasedOnPermission(permissions);
        });
      }
      redirectBasedOnPermission(permissions);
    }

    function redirectBasedOnPermission(permissions) {
      if (!permissions[requiredPermission]) {
        // authenticated, but lack access to this page
        history.replace({ pathname: '/404' });
      }
    }
  };
}

// e.g. finishing onboarding, become authenticated, go somewhere within dashboard
function redirectPublicPathToAuthedPath(store, authedPath = '/dashboard') {
  return ({ history, location }) => {
    const {
      user: { user },
    } = store.getState();
    if (user) {
      history.replace({ pathname: authedPath });
    }
  };
}

function setCanonicalPathToEpisodeAndSetReferralCodeWithStore(store, baseUrl) {
  return props => {
    setCanonicalPathToEpisode(props);
    setReferralCodeForEpisode(props, store, baseUrl);
  };
}

function setReferralCodeForEpisodeWithStore(store, baseUrl) {
  return props => {
    setReferralCodeForEpisode(props, store, baseUrl);
  };
}

function redirectRoute(store, path) {
  return withOnEnter(redirectToPath(store, path), RedirectingInterstitial);
}

function setCanonicalPathToEpisode({ route, location }) {
  const splitPath = location.pathname.split('/');
  splitPath.pop();
  route.canonicalPath = splitPath.join('/');
}

function setReferralCodeForEpisode(
  { location, match: { params } },
  store,
  baseUrl
) {
  const metadata = getMetadataFromParams(params);
  const { episodeIdHash } = metadata;
  saveReferralIp(baseUrl, {
    location,
    episodeId: episodeIdHash,
  });
}
